import { PERMISSIONS } from '@core/core.public-api';

import type { NavItem, NavSection } from './nav-item.model';

/**
 * Fuente unica de la navegacion del backoffice. Mantener en sync con app.routes.ts:
 * mismo `path` y mismo `permission`. Los permisos referencian el catalogo canonico PERMISSIONS
 * (core/rbac) que el PermissionService resuelve desde los claims del token.
 * `permission: null` => visible para todo usuario autenticado (no hay permiso dedicado).
 */
export const NAV_SECTIONS: readonly NavSection[] = [
  {
    title: 'Operacion',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: 'dashboard', permission: null },
      { path: '/live-map', label: 'Mapa en vivo', icon: 'map', permission: PERMISSIONS.LIVEMAP_READ },
      { path: '/trips', label: 'Viajes', icon: 'route', permission: PERMISSIONS.TRIPS_READ },
      { path: '/bookings', label: 'Reservas / Cupos', icon: 'event-seat', permission: PERMISSIONS.BOOKINGS_READ },
      { path: '/telemetry', label: 'Alertas de conduccion', icon: 'sensors', permission: PERMISSIONS.TELEMETRY_READ },
      { path: '/mdvr', label: 'Visor MDVR', icon: 'videocam', permission: PERMISSIONS.MDVR_READ },
    ],
  },
  {
    title: 'Flota y personas',
    items: [
      { path: '/fleet', label: 'Flota', icon: 'directions-bus', permission: PERMISSIONS.FLEET_READ },
      { path: '/drivers', label: 'Choferes (Anexo E)', icon: 'badge', permission: PERMISSIONS.DRIVERS_READ },
      { path: '/documents', label: 'Centro documental', icon: 'folder-shared', permission: PERMISSIONS.DOCUMENTS_READ },
      { path: '/routes', label: 'Rutas y tramos', icon: 'alt-route', permission: PERMISSIONS.ROUTE_READ },
    ],
  },
  {
    title: 'Cumplimiento',
    items: [
      { path: '/checklists', label: 'Checklists pre-viaje', icon: 'checklist', permission: PERMISSIONS.CHECKLIST_READ },
      {
        path: '/journey-night',
        label: 'Journey nocturno (Anexo F)',
        icon: 'nights-stay',
        // QA-004: visible para quien APRUEBA (journey:approve) o SOLICITA (trip:write,
        // p.ej. operador) — el OR del isVisible del sidenav.
        permission: PERMISSIONS.JOURNEY_APPROVE,
        anyOfPermissions: [PERMISSIONS.JOURNEY_CREATE],
      },
      { path: '/incidents', label: 'Incidentes / Contingencia', icon: 'report', permission: PERMISSIONS.INCIDENTS_READ },
      {
        path: '/hse-eps',
        label: 'HSE / EPS',
        icon: 'health-and-safety',
        permission: PERMISSIONS.HSE_READ,
        // El servicio medico (eps:read) NO tiene hse:read pero es el unico rol
        // que ve los datos EPS — mismo OR que el permissionGuardAny de la ruta.
        anyOfPermissions: [PERMISSIONS.EPS_HEALTH_READ],
        sensitive: true,
      },
      { path: '/discipline', label: 'Disciplina (Anexo G)', icon: 'gavel', permission: PERMISSIONS.DISCIPLINE_READ },
      {
        path: '/compliance',
        label: 'Cumplimiento documental',
        icon: 'lock',
        permission: PERMISSIONS.COMPLIANCE_DASHBOARD_READ,
      },
    ],
  },
  {
    title: 'Gestion',
    items: [
      { path: '/kpis', label: 'KPI / SLA', icon: 'insights', permission: PERMISSIONS.KPIS_READ },
      { path: '/reports', label: 'Reporteria', icon: 'summarize', permission: PERMISSIONS.REPORTS_READ },
      { path: '/billing', label: 'Facturacion / Certificacion', icon: 'receipt-long', permission: PERMISSIONS.BILLING_READ },
      { path: '/penalties', label: 'Penalidades', icon: 'gavel', permission: PERMISSIONS.PENALTIES_READ },
      // La campana del topbar es el acceso primario; este item es el atajo con ruta
      // propia. Gated en notifications:read (el backend lo exige en GET /notifications).
      { path: '/notifications', label: 'Notificaciones', icon: 'bell', permission: PERMISSIONS.NOTIFICATIONS_READ },
    ],
  },
  {
    title: 'Administracion',
    items: [
      { path: '/admin', label: 'Config del tenant', icon: 'settings', permission: PERMISSIONS.ADMIN_USERS },
      // Solo super-admin PVP (admin:tenants viaja en su JWT): alta de empresas (LION-864).
      { path: '/empresas', label: 'Empresas', icon: 'truck', permission: PERMISSIONS.ADMIN_TENANTS },
    ],
  },
];

/**
 * Visibilidad de un item de navegacion segun los permisos del usuario.
 * FUENTE UNICA de la poda por permiso: la consumen el sidenav y el estado de
 * bienvenida del dashboard (R2-029) para que ambos muestren EXACTAMENTE las
 * mismas secciones. `hasPermission` se inyecta como funcion para que el caller
 * decida la fuente (Signal de PermissionService dentro de un computed).
 */
export function isNavItemVisible(item: NavItem, hasPermission: (p: string) => boolean): boolean {
  if (item.permission === null) {
    return true;
  }
  if (hasPermission(item.permission)) {
    return true;
  }
  // OR declarativo: alcanza con alguno de los permisos alternativos.
  return (item.anyOfPermissions ?? []).some((p) => hasPermission(p));
}

/** Secciones con sus items filtrados por permiso; descarta las secciones vacias. */
export function visibleNavSections(hasPermission: (p: string) => boolean): readonly NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    title: section.title,
    items: section.items.filter((item) => isNavItemVisible(item, hasPermission)),
  })).filter((section) => section.items.length > 0);
}
